import { DetailView } from '@components/enitity'
import { Loader } from '@components/loader/Loader'
import FlatPayments from '@features/flats/components/FlatPayments'
import { FlatSettlements } from '@features/flats/components/FlatSettlements'
import { FlatsChecklist } from '@features/flats/components/FlatsChecklist'
import { FlatsContracts } from '@features/flats/components/FlatsContracts'
import { FlatsGallery } from '@features/flats/components/FlatsGallery'
import { FlatsWidget } from '@features/flats/components/FlatsWidget'
import { OffersTable } from '@features/offers'
import { useFlatsDetail } from '@services/flats/queries/useFlatsDetail'
import {
  FileText,
  Images,
  Info,
  ListChecks,
  type LucideIcon,
  Pencil,
  Receipt,
  Tag,
  Wallet
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { RentModel } from 'types/api'

import { cn } from '@/lib/utils'

import { FlatsEdit } from './FlatsEdit'

export function FlatsDetail() {
  const { id } = useParams()
  const { t } = useTranslation('flat')
  const [searchParams, setSearchParams] = useSearchParams()
  const { data, isLoading } = useFlatsDetail({ id })

  if (isLoading) {
    return <Loader />
  }

  if (!data) {
    return <div>Flat not found</div>
  }

  const tabs: { key: string; label: string; icon: LucideIcon }[] = [
    { key: 'detail', label: t('navigation.detail'), icon: Info },
    { key: 'galeria', label: t('navigation.gallery'), icon: Images },
    { key: 'checklist', label: t('navigation.checklist'), icon: ListChecks },
    { key: 'contract', label: t('navigation.contract'), icon: FileText },
    { key: 'payments', label: t('navigation.payments'), icon: Wallet },
    { key: 'settlements', label: t('navigation.settlements'), icon: Receipt },
    { key: 'offers', label: t('navigation.offers'), icon: Tag },
    { key: 'edit', label: t('navigation.edit'), icon: Pencil }
  ]

  const active = searchParams.get('tab') || 'detail'
  const setActive = (key: string) =>
    setSearchParams(
      (prev) => {
        prev.set('tab', key)
        return prev
      },
      { replace: true }
    )

  const panels: Record<string, React.ReactNode> = {
    detail: <FlatsWidget flat={data} />,
    galeria: <FlatsGallery data={data.gallery} />,
    checklist: <FlatsChecklist flat={data} />,
    contract: <FlatsContracts flat={data} />,
    payments: (
      <FlatPayments
        flat={data}
        rents={data.flat_payments as unknown as RentModel[]}
      />
    ),
    settlements: <FlatSettlements flat={data} />,
    offers: (
      <div>
        <h2 className="m-0 mb-6 flex items-center gap-2.5 font-display text-xl font-bold tracking-tight text-ink">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Tag className="h-5 w-5" />
          </span>
          {t('navigation.offers')}
        </h2>
        <OffersTable flat={data?.id} />
      </div>
    ),
    edit: <FlatsEdit data={data} />
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb header */}
      <DetailView.Breadcrumb
        parentLabel={t('title')}
        parentTo="/admin/flats"
        current={data.title}
      />

      {/* Tab bar */}
      <div className="-mx-1 overflow-x-auto px-1">
        <div className="inline-flex items-center gap-1 rounded-full border border-ink/[0.06] bg-ink/[0.03] p-1">
          {tabs.map(({ key, label, icon: Icon }) => {
            const isTabActive = active === key
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActive(key)}
                className={cn(
                  'flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border-0 px-4 py-2 text-sm font-semibold transition-colors',
                  isTabActive
                    ? 'bg-white text-ink shadow-sm'
                    : 'bg-transparent text-muted-foreground hover:text-ink'
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Active panel */}
      <div>{panels[active] ?? panels.detail}</div>
    </div>
  )
}
